import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Switch,
  TextField,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import { getBindings } from "app/services/api/bindings";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";

const DSOKProductAddNewBindingModal = ({
  values,
  setFieldValue,
  onSave,
  onCancel,
  editingId,
  initialBinding,
}) => {
  const { t } = useTranslation();
  const { getAuthUser } = useJumboAuth();
  const { customer } = getAuthUser();
  const defaultLanguage = React.useMemo(() => {
    const langs = customer?.dsokSettings?.customer?.languages;
    return Array.isArray(langs) && langs.length ? langs[0] : "sk";
  }, [customer]);

  const [bindings, setBindings] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [showValidation, setShowValidation] = React.useState(false);
  const [selectedIdLocal, setSelectedIdLocal] = React.useState(() => {
    const v = values?.bindings?.bindingId;
    return v != null && v !== "" ? String(v) : "";
  });
  const [selectedLabel, setSelectedLabel] = React.useState("");
  const [localMin, setLocalMin] = React.useState(values?.bindings?.min ?? 0);
  const [localMax, setLocalMax] = React.useState(values?.bindings?.max ?? 0);
  const [localRequired, setLocalRequired] = React.useState(
    Boolean(values?.bindings?.required)
  );
  const [localNameObj, setLocalNameObj] = React.useState(
    (values?.bindings?.name && typeof values.bindings.name === "object"
      ? values.bindings.name
      : { sk: "", en: "" }) || { sk: "", en: "" }
  );

  // Prefill from initialBinding (passed when opening Edit) or from values.bindings[editingId]
  React.useEffect(() => {
    const existing =
      initialBinding ||
      (editingId && values?.bindings?.[editingId]
        ? values.bindings[editingId]
        : null);
    if (existing && typeof existing === "object") {
      const nextBindingId = String(existing.bindingId ?? "");
      const nextRequired = Boolean(existing.required);
      const nextMin = existing.min ?? 0;
      const nextMax = existing.max ?? 0;

      setSelectedIdLocal(nextBindingId);
      setLocalRequired(nextRequired);
      setLocalMin(nextMin);
      setLocalMax(nextMax);
      setFieldValue("bindings.bindingId", nextBindingId);
      setFieldValue("bindings.required", nextRequired);
      setFieldValue("bindings.min", nextMin);
      setFieldValue("bindings.max", nextMax);

      const nameObj =
        existing.name && typeof existing.name === "object"
          ? existing.name
          : null;
      if (nameObj && Object.values(nameObj).some((v) => v != null && String(v).trim() !== "")) {
        setFieldValue("bindings.name", nameObj);
        setLocalNameObj(nameObj);
        const label =
          nameObj[defaultLanguage] ||
          nameObj.sk ||
          nameObj.en ||
          nameObj.SK ||
          nameObj.EN ||
          Object.values(nameObj).find((v) => typeof v === "string" && v.trim()) ||
          "";
        setSelectedLabel(String(label || "").trim());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId, initialBinding]);

  // When bindings list is loaded and we have bindingId but no name, resolve name from API list
  React.useEffect(() => {
    if (!selectedIdLocal || !Array.isArray(bindings) || bindings.length === 0) return;
    const hasName =
      localNameObj &&
      typeof localNameObj === "object" &&
      Object.values(localNameObj).some((v) => v != null && String(v).trim() !== "");
    if (hasName) return;
    const found = bindings.find((b) => String(b._id) === String(selectedIdLocal));
    if (!found) return;
    const src =
      (found.bindingName && typeof found.bindingName === "object" && found.bindingName) ||
      (found.name && typeof found.name === "object" && found.name) ||
      {};
    const nameObj = {
      sk: src.sk || src.SK || "",
      en: src.en || src.EN || "",
    };
    if (nameObj.sk || nameObj.en) {
      setLocalNameObj(nameObj);
      setFieldValue("bindings.name", nameObj);
    }
  }, [bindings, selectedIdLocal, localNameObj, setFieldValue]);

  // Keep local selected value in sync with Formik on external changes (when not set locally)
  React.useEffect(() => {
    if (!selectedIdLocal) {
      const v = values?.bindings?.bindingId;
      setSelectedIdLocal(v != null && v !== "" ? String(v) : "");
    }
  }, [values?.bindings?.bindingId, selectedIdLocal]);

  // Auto-select by name when bindings arrive and id is missing
  React.useEffect(() => {
    if (selectedIdLocal) return;
    const currentId = values?.bindings?.bindingId;
    if (currentId) {
      setSelectedIdLocal(currentId);
      return;
    }
    const nm = values?.bindings?.name;
    if (!nm || typeof nm !== "object") return;
    const wanted =
      nm?.[defaultLanguage] ||
      nm?.[String(defaultLanguage).toUpperCase()] ||
      nm?.sk ||
      nm?.SK ||
      nm?.en ||
      nm?.EN ||
      Object.values(nm).find((v) => typeof v === "string" && v.trim());
    if (!wanted) return;
    const match = (bindings || []).find((b) => {
      const src =
        (b.bindingName && typeof b.bindingName === "object" && b.bindingName) ||
        (b.name && typeof b.name === "object" && b.name) ||
        {};
      const candidate =
        src?.[defaultLanguage] ||
        src?.[String(defaultLanguage).toUpperCase()] ||
        src?.sk ||
        src?.SK ||
        src?.en ||
        src?.EN ||
        Object.values(src).find((v) => typeof v === "string" && v.trim());
      return String(candidate || "").trim() === String(wanted || "").trim();
    });
    if (match && match._id) {
      setSelectedIdLocal(match._id);
      setFieldValue("bindings.bindingId", match._id);
    }
  }, [
    bindings,
    defaultLanguage,
    selectedIdLocal,
    values?.bindings?.name,
    values?.bindings?.bindingId,
    setFieldValue,
  ]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const list = await getBindings();
        if (!mounted) return;
        setBindings(Array.isArray(list) ? list : []);
      } catch (_e) {
        if (!mounted) return;
        setBindings([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleSelectBinding = (id) => {
    const idStr = id != null && id !== "" ? String(id) : "";
    setSelectedIdLocal(idStr);
    setFieldValue("bindings.bindingId", idStr);
    const found = (bindings || []).find((b) => String(b._id) === idStr);
    const src =
      (found && typeof found.bindingName === "object" && found.bindingName) ||
      (found && typeof found.name === "object" && found.name) ||
      {};
    const nameObj = {
      sk: src.sk || src.SK || "",
      en: src.en || src.EN || "",
    };
    // Store only locally during edit; commit to Formik on Save
    setLocalNameObj(nameObj);
    setSelectedLabel(
      nameObj?.[defaultLanguage] ||
        nameObj?.[String(defaultLanguage).toUpperCase()] ||
        nameObj.sk ||
        nameObj.en ||
        ""
    );
    if (showValidation) {
      setShowValidation(false);
    }
  };

  const selectedId = selectedIdLocal != null && selectedIdLocal !== "" ? String(selectedIdLocal) : "";

  const resolveBindingLabel = React.useCallback(
    (b) => {
      if (!b) return "";
      const bn = b.bindingName;
      const nameObj =
        (bn && typeof bn === "object" && bn) ||
        (b.name && typeof b.name === "object" && b.name) ||
        null;
      if (nameObj) {
        return (
          nameObj?.[defaultLanguage] ||
          nameObj?.[String(defaultLanguage).toUpperCase()] ||
          nameObj?.sk ||
          nameObj?.SK ||
          nameObj?.en ||
          nameObj?.EN ||
          Object.values(nameObj).find(
            (v) => typeof v === "string" && v.trim()
          ) ||
          ""
        );
      }
      if (typeof b.name === "string") return b.name;
      return b._id || "";
    },
    [defaultLanguage]
  );

  // Keep a stable label to render even when form fields change
  React.useEffect(() => {
    if (selectedIdLocal) {
      const found = (bindings || []).find(
        (b) => String(b._id) === String(selectedIdLocal)
      );
      const label = resolveBindingLabel(found);
      if (label) {
        setSelectedLabel(label);
        return;
      }
    }
    const nm = localNameObj || values?.bindings?.name;
    if (nm && typeof nm === "object") {
      const label =
        nm?.[defaultLanguage] ||
        nm?.[String(defaultLanguage).toUpperCase()] ||
        nm?.sk ||
        nm?.SK ||
        nm?.en ||
        nm?.EN ||
        Object.values(nm).find((v) => typeof v === "string" && v.trim()) ||
        "";
      setSelectedLabel(label || "");
    }
  }, [
    bindings,
    selectedIdLocal,
    localNameObj,
    values?.bindings?.name,
    defaultLanguage,
    resolveBindingLabel,
  ]);

  return (
    <>
      <Box>
        <Grid container columnSpacing={2} ml={1}>
          <Grid item xs={12} md={8} lg={4}>
            <Div sx={{ mt: 2, mb: 1, width: "100%" }}>
              <FormControl
                fullWidth
                required
                error={showValidation && !selectedId}
                disabled={loading}
              >
                <InputLabel id="binding-select-label">
                  {t("pages.dsokProducts.binding", { defaultValue: "Binding" })}
                </InputLabel>
                <Select
                  labelId="binding-select-label"
                  id="binding-select"
                  value={selectedId}
                  label={t("pages.dsokProducts.binding", {
                    defaultValue: "Binding",
                  })}
                  displayEmpty
                  renderValue={(value) => {
                    const renderFromName = () => {
                      const nm = localNameObj || values?.bindings?.name;
                      if (nm && typeof nm === "object") {
                        return (
                          selectedLabel ||
                          nm?.[defaultLanguage] ||
                          nm?.sk ||
                          nm?.en ||
                          Object.values(nm).find(
                            (v) => typeof v === "string" && v.trim()
                          ) ||
                          ""
                        );
                      }
                      if (typeof values?.bindings?.name === "string") {
                        return values.bindings.name;
                      }
                      return selectedLabel || "";
                    };

                    if (!value) {
                      return selectedLabel || renderFromName();
                    }
                    const found = (bindings || []).find(
                      (b) => String(b._id) === String(value)
                    );
                    return found
                      ? resolveBindingLabel(found)
                      : selectedLabel || renderFromName();
                  }}
                  onChange={(e) => handleSelectBinding(e.target.value)}
                >
                  {(bindings || []).map((b) => {
                    const id = b._id != null ? String(b._id) : "";
                    return (
                      <MenuItem key={id} value={id}>
                        {resolveBindingLabel(b)}
                      </MenuItem>
                    );
                  })}
                </Select>
                {showValidation && !selectedId ? (
                  <FormHelperText>
                    {t("validation.required", {
                      defaultValue: "Required field",
                    })}
                  </FormHelperText>
                ) : null}
              </FormControl>
            </Div>
          </Grid>
          <Grid item md={4} xs={12} lg={4}>
            <Div sx={{ mt: 2, mb: 1, width: "100%" }}>
              <TextField
                type="number"
                fullWidth
                value={localMin}
                label={t("pages.dsokProducts.min")}
                InputLabelProps={{ required: false }}
                inputProps={{ min: 0 }}
                onChange={(e) => {
                  const v = e.target.value === "" ? "" : Number(e.target.value);
                  setLocalMin(v);
                  setFieldValue(`bindings.min`, v);
                }}
              />
            </Div>
          </Grid>
          <Grid item md={4} xs={12} lg={4}>
            <Div sx={{ mt: 2, mb: 1, width: "100%" }}>
              <TextField
                type="number"
                fullWidth
                value={localMax}
                label={t("pages.dsokProducts.max")}
                InputLabelProps={{ required: false }}
                inputProps={{ min: 0 }}
                onChange={(e) => {
                  const v = e.target.value === "" ? "" : Number(e.target.value);
                  setLocalMax(v);
                  setFieldValue(`bindings.max`, v);
                }}
              />
            </Div>
          </Grid>
          <Grid item xs={12} md={8} lg={4}>
            <Div sx={{ mt: 1, mb: 1, width: "100%" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(localRequired)}
                    onChange={(e) => {
                      setLocalRequired(e.target.checked);
                      setFieldValue("bindings.required", e.target.checked);
                    }}
                  />
                }
                label={t("pages.dsokProducts.required", {
                  defaultValue: "Required",
                })}
              />
            </Div>
          </Grid>
        </Grid>
      </Box>
      <Div
        sx={{
          mt: 2,
          mb: 1,
          mx: 2,
          gap: "4px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button size="normal" onClick={onCancel}>
          {t("buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          size="normal"
          onClick={() => {
            if (!selectedId) {
              setShowValidation(true);
              return;
            }
            // Pass current modal values to onSave so parent gets them synchronously
            // (Formik setFieldValue is async; reading values in parent would be stale)
            onSave({
              name: localNameObj,
              min: localMin ?? 0,
              max: localMax ?? 0,
              required: Boolean(localRequired),
              bindingId: selectedIdLocal,
            });
          }}
        >
          {t("buttons.ok")}
        </Button>
      </Div>
    </>
  );
};

export default DSOKProductAddNewBindingModal;
